/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Resolve,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import { IAccountData } from '@app/models/account-data.model';
import { ILoan } from '@app/models/loan.model';
import { LoanStatus } from '@app/models/Loan-Status';
import { ILosInterface } from '@app/models/los-interface.model';
import { IUserAccMarketPlace } from '@app/models/user-info.model';
import { updateHubData } from '@app/shared/utils/hubData';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';

import { AccountService } from './account.service';
import { BookingService } from './booking.service';
import { FiProductsService } from './fi-products.service';
import { LoanInfoService } from './loan-info.service';
import { LoggingService } from './logging.service';
import { LosInterfaceService } from './los-interface.service';
import { ProposalService } from './proposal.service';
import { UnityDirectStateService } from './unity-direct-state.service';
import { UserInfoService } from './user-info.service';

@Injectable({ providedIn: 'root' })
export class BundlesPageResolver implements Resolve<any> {
  eLoanStatus = LoanStatus;
  constructor(
    private router: Router,
    private userInfoService: UserInfoService,
    private loanInfoService: LoanInfoService,
    private losInterfaceService: LosInterfaceService,
    private accountService: AccountService,
    private proposalService: ProposalService,
    private bookingService: BookingService,
    private fiProductsService: FiProductsService,
    private ls: LoggingService,
    private udStateService: UnityDirectStateService
  ) {}

  resolve(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): any {
    const id: string = this.router.getCurrentNavigation()?.extras.state?.id;
    const status: LoanStatus =
      this.router.getCurrentNavigation()?.extras.state?.status;
    this.udStateService.proposalId = id;
    return this.userInfoService.getUserInfo().pipe(
      concatMap((userInfoData: IUserAccMarketPlace) => {
        const getStatusCall = this.losInterfaceService
          .getLoanStatus(
            userInfoData[this.userInfoService.getAccountIndex(userInfoData)]
          )
          .pipe(catchError(() => of(<ILosInterface>{})));
        const getCUNameCall = this.accountService.getAccount(
          userInfoData[this.userInfoService.getAccountIndex(userInfoData)]
        );
        const getUserInfo = of(userInfoData);
        return forkJoin([getStatusCall, getCUNameCall, getUserInfo]);
      }),
      concatMap(([statusInfo, ...remaining]) => {
        if (statusInfo.loanStatus === this.eLoanStatus.Proposal) {
          this.udStateService.proposalId = statusInfo.quoteLoanId;
          return combineLatest(
            of([statusInfo, ...remaining]),
            this.proposalService.getRatesForProposal(statusInfo.quoteLoanId),
            this.proposalService.getProposal({ id: statusInfo.quoteLoanId })
          ) as Observable<
            [[ILosInterface, IAccountData, IUserAccMarketPlace], any, ILoan]
          >;
        } else if (
          statusInfo.loanStatus === 'Booked' ||
          statusInfo.loanStatus === this.eLoanStatus.Booking ||
          statusInfo.loanStatus === this.eLoanStatus.Void
        ) {
          return combineLatest(
            of([statusInfo, ...remaining]),
            of({}),
            this.bookingService.getBooking({ id: statusInfo.quoteLoanId })
          ) as Observable<
            [[ILosInterface, IAccountData, IUserAccMarketPlace], {}, ILoan]
          >;
        } else if (status === undefined || status === null) {
          return of([[statusInfo, ...remaining], null, null]);
        }
      }),
      map(([[statusInfo, accountInfo, userInfo], proposalRates, loan]) => {
        // To populate the data on header before page is loaded
        this.loanInfoService.loanInfo.next({
          loName: `${
            userInfo[
              this.userInfoService.getAccountIndex(
                userInfo as IUserAccMarketPlace
              )
            ].AccountName
          }`,
          loanStatus: (statusInfo as ILosInterface).loanStatus,
          creditUnionName: (accountInfo as IAccountData).fiName
        });
        return { proposalRates, loan, statusInfo, userInfo, accountInfo };
      }),
      concatMap(
        ({ proposalRates, loan, statusInfo, userInfo, accountInfo }) => {
          sessionStorage.setItem('portalAccountId', userInfo[0].AccountId);
          const hubData = this.proposalService.getProposalFromHubData();
          if (loan?.['loanData']?.['interestRate']) {
            hubData.loanData.interestRate = loan?.['loanData'].interestRate;
          }
          updateHubData(hubData);
          return forkJoin({
            fiProducts: this.fiProductsService.getFiProducts(
              userInfo[
                this.userInfoService.getAccountIndex(
                  userInfo as IUserAccMarketPlace
                )
              ]
            ),
            bundlesRes: [{}]
          }).pipe(
            map(({ fiProducts, bundlesRes }) => ({
              fiProducts,
              bundlesRes,
              proposalRates,
              loan,
              statusInfo,
              userInfo,
              accountInfo
            }))
          );
        }
      ),
      catchError(() => {
        //Redirecting to error page since we couldn't load the page without resolver data
        this.ls.sendErrorLevelMessage('Bundles Resolver Error', this, {
          error: 'There was an error completing the bundles resolver.'
        });
        return of(null);
      })
    );
  }
}
